import { OrganisationProductSummary } from "@fero/components";
import { MINIMUM_SEARCH_TERM_LENGTH, useProductSearchQuery } from "@fero/hooks";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  CircularProgress,
  InputAdornment,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { WithDbId } from "adl-gen/common/db";
import type { Product } from "adl-gen/ferovinum/app/db";
import React, { type SyntheticEvent, useCallback, useState } from "react";

interface ProductSearchBarProps {
  onSelectProduct: (product: WithDbId<Product>) => void;
  selectedProductCodes: Set<string>;
}

const NoOptionsText = () => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Typography variant="body1">{"Product could not be found."}</Typography>
  </Stack>
);

const AutocompleteInput: React.FC<
  AutocompleteRenderInputParams & { isLoading: boolean }
> = (props) => (
  <TextField
    {...props}
    label="Search by product code, name or producer"
    fullWidth
    InputProps={{
      ...props.InputProps,
      startAdornment: (
        <InputAdornment position="start">
          <SearchOutlinedIcon />
        </InputAdornment>
      ),
      endAdornment: (
        <>
          {props.isLoading ? (
            <CircularProgress color="inherit" size={20} />
          ) : null}
          {props.InputProps.endAdornment}
        </>
      ),
    }}
  />
);

interface ProductSearchBarOptionProps {
  product: WithDbId<Product>;
  onClick: (productId: WithDbId<Product>) => void;
}
const ProductSearchBarOption: React.FC<ProductSearchBarOptionProps> = (
  props,
) => {
  const { product, onClick } = props;
  const productId = product.id;
  const { alcoholByVolumePc, countryOfOrigin } = product.value;

  const onSelectProduct = () => {
    onClick(product);
  };

  return (
    <React.Fragment key={productId}>
      <ListItem {...props} sx={{ pl: 4 }} onClick={onSelectProduct}>
        <Box sx={{ p: 2 }}>
          <OrganisationProductSummary
            {...props.product.value}
            abv={alcoholByVolumePc}
            country={countryOfOrigin}
          />
        </Box>
      </ListItem>
    </React.Fragment>
  );
};

/**
 * TODO ND pull out into a reusable component - this is duplicated from the trade sale search and select work
 * https://linear.app/ferovinum/issue/FERO-153/combine-product-search-components
 */
export const ProductSearchBar: React.FC<ProductSearchBarProps> = (props) => {
  const { onSelectProduct, selectedProductCodes } = props;

  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState<WithDbId<Product> | null>(
    null,
  );
  const { isFetching, data } = useProductSearchQuery(inputValue);

  const isAutocompleteShown =
    inputValue.trim().length >= MINIMUM_SEARCH_TERM_LENGTH;
  const filterOptions = useCallback(
    (products: WithDbId<Product>[]) =>
      products.filter((p) => !selectedProductCodes.has(p.value.code)),
    [selectedProductCodes],
  );

  return (
    <Autocomplete<WithDbId<Product>>
      filterOptions={filterOptions}
      value={selectedValue}
      inputValue={inputValue}
      noOptionsText={<NoOptionsText />}
      getOptionLabel={(p) => p.value.name}
      loading={isFetching}
      renderInput={(args) => (
        <AutocompleteInput {...args} isLoading={isFetching} />
      )}
      options={data || []}
      onInputChange={(_event: SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      sx={{ width: "50%" }}
      open={isAutocompleteShown}
      renderOption={(props, product) => (
        <ProductSearchBarOption
          {...props}
          product={product}
          onClick={(product) => {
            if (product) {
              // Clear the selected value immediately so that a user can easily start typing again
              setSelectedValue(null);
              setInputValue("");

              // Let any parent components know which product was selected
              onSelectProduct(product);
            }
          }}
        />
      )}
    />
  );
};
